import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { match } from 'ts-pattern'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { useNavigation } from '@/app/hooks'
import useExternalNavigation from '@/app/hooks/useExternalNavigation'
import { MISSED_CHARGE_TARGET_HELP_URL } from '@/utils/helpCenterUrls'
import { logEvent } from '@/logging'
import { submitContactFormWithAuth } from '@/actions/form'
import { chargeForecastCollection } from '@/reducers/chargeRelatedCollections'
import { vehicleActiveChargeSettingsCollection } from '@/reducers/vehicleActiveChargeSettings'
import { vehicleConnectivityCollection } from '@/reducers/vehicleConnectivity'
import { vehicleDemandResponseEventsCollection } from '@/reducers/vehicleDemandResponseEvents'
import useVehicleChargeSchedulesFetcher from '@/authenticated/hooks/useVehicleChargeSchedulesFetcher'
import selectUserHasPendingPlan from '@/selectors/selectUserHasPendingPlan'
import { selectVehicleIsParticipatingInActiveEvent } from '@/selectors/vehicleDemandResponseEvent'
import { selectNextDeviceDREvent } from '@/selectors/vehicleDemandResponseEvent/selectNextVehicleDREvent'
import { eventIsCurrentlyActive } from '@/types/deviceDemandResponseEvent'
import { RequestStatus } from '@/request/types'
import { convertKilometersToMiles } from '@/utils/conversionUtilities'
import { ChargeSchedule } from '@/types'
import { ActiveChargeSettingsFields } from '@/types/activeChargeSettings'
import { Vehicle } from '@/types/vehicle'
import { StatusAlertAction } from './ChargeForecastV2.types'
import {
  ActiveChargeFlags,
  ReportTopic,
  getChargeStatus,
  getHasDeviceAlertBanner,
  getShowChargeButton,
} from './ChargeStatus.utils'
import {
  getChargeSegments,
  getChargingWindow,
} from './ChargeSessionDetails.utils'
import { getChargeChart } from './ChargeSessionChart.utils'
import {
  getRatePlanKind,
  getRealTimeBoundariesCents,
  getUpcomingRates,
} from './UpcomingRatesCard.utils'

export const UPCOMING_RATES_ID = 'charge-forecast-v2-upcoming-rates'

/**
 * Support sees one ticket subject per banner, so a report says which failure
 * the customer hit rather than a generic "unavailable".
 */
export const REPORT_CONTEXT: Record<
  ReportTopic,
  { subject: string; detail: string }
> = {
  forecastFailed: {
    subject: 'Feedback: Charge Forecast unavailable',
    detail:
      'Reported from the Charge Forecast card: the forecast request failed.',
  },
  forecastEmpty: {
    subject: 'Feedback: Charge Forecast has no charge plan',
    detail:
      'Reported from the Charge Forecast card: the forecast came back with no hours, which usually means missing car data or missing rate prices.',
  },
  batteryUnknown: {
    subject: 'Feedback: Charge Forecast missing battery level',
    detail:
      'Reported from the Charge Forecast card: the car has not reported a battery level.',
  },
}

const DEFAULT_CHARGE_LIMIT_PERCENT = 80

/**
 * The charge limit as a battery percent. Schedules can store it as a range
 * (miles or kilometers), which converts through the car's miles per percent.
 */
export function getChargeLimitPercent(
  chargeSchedule: ChargeSchedule | undefined,
  vehicle: Vehicle | undefined,
) {
  const fallback = vehicle?.charge_limit_percent ?? DEFAULT_CHARGE_LIMIT_PERCENT
  const milesPerPercent = vehicle?.miles_per_battery_percent

  if (!chargeSchedule) {
    return fallback
  }

  return match(chargeSchedule.battery_target_unit)
    .with('percent', () => chargeSchedule.battery_target_max)
    .with('miles', () =>
      milesPerPercent
        ? Math.round(chargeSchedule.battery_target_max / milesPerPercent)
        : fallback,
    )
    .with('kilometers', () =>
      milesPerPercent
        ? Math.round(
            convertKilometersToMiles(chargeSchedule.battery_target_max) /
              milesPerPercent,
          )
        : fallback,
    )
    .exhaustive()
}

/**
 * The goals the backend is optimizing to. A program with a shadow rate
 * (PSEG-LI, PNM) turns these on without touching the saved charge schedule,
 * so the schedule alone would read as "nothing is optimizing".
 */
export function getActiveChargeFlags(
  settings: ActiveChargeSettingsFields | undefined,
): ActiveChargeFlags | undefined {
  return settings
    ? {
        moneySaving: settings.money_saving_plan_enabled.value,
        savePlanet: settings.save_the_planet_plan_enabled.value,
        solarSurplus: settings.solar_surplus_plan_enabled.value,
        scheduleDeparture: settings.schedule_departure_enabled.value,
        allowChargingControl: settings.allow_charging_control.value,
      }
    : undefined
}

export function useChargeForecastV2ViewModel({
  vehicleId,
}: {
  vehicleId: number
}) {
  const dispatch = useAppDispatch()
  const navigation = useNavigation()
  const { externallyNavigate } = useExternalNavigation()
  const [whyModalOpen, setWhyModalOpen] = useState(false)
  const [reportIssueOpen, setReportIssueOpen] = useState(false)
  const [reportIssueSent, setReportIssueSent] = useState(false)
  // While the driver drags the limit, chart the new limit before it saves
  const [draggedChargeLimit, setDraggedChargeLimit] = useState<number>()

  const { data: chargeForecasts, status: chargeForecastStatus } =
    chargeForecastCollection.useFetch({
      params: { vehicle_id: vehicleId },
    })
  const { data: activeChargeSettings } =
    vehicleActiveChargeSettingsCollection.useFetch(String(vehicleId))
  const { data: connectivity } = vehicleConnectivityCollection.useFetch(
    `vehicle/${vehicleId}/connectivity`,
  )
  vehicleDemandResponseEventsCollection.useFetch({
    params: { vehicle_id: vehicleId },
  })
  const chargeSchedule = useVehicleChargeSchedulesFetcher()?.find(
    (schedule) => schedule.vehicle_id === vehicleId,
  )
  const vehicle = useAppSelector((state) =>
    state.vehicles.vehicles?.find((v) => v.id === vehicleId),
  )
  const hasPendingRatePlan = useAppSelector(selectUserHasPendingPlan)
  const isInActiveDREvent = useAppSelector((state) =>
    selectVehicleIsParticipatingInActiveEvent(state, vehicleId),
  )
  const deviceDREvent = useAppSelector((state) =>
    selectNextDeviceDREvent(state, vehicleId, 'vehicle'),
  )
  const hasDREventAlert = Boolean(deviceDREvent)
  const optedOutOfActiveEvent = Boolean(
    deviceDREvent &&
      !deviceDREvent.enrolled &&
      eventIsCurrentlyActive(deviceDREvent),
  )

  useEffect(() => {
    logEvent('chargeforecast_v2_seen')
  }, [])

  const now = dayjs()
  const forecastForVehicle =
    chargeForecasts?.find((forecast) => forecast.vehicle_id === vehicleId) ??
    chargeForecasts?.[0]
  const chargeForecast =
    forecastForVehicle?.type === 'no home' ? undefined : forecastForVehicle
  const chargeLimitPercent =
    draggedChargeLimit ?? getChargeLimitPercent(chargeSchedule, vehicle)
  const batteryPercent = vehicle?.battery_level_percent ?? null

  const status = getChargeStatus({
    chargeForecast,
    forecastFailed: chargeForecastStatus === RequestStatus.Failed,
    chargeSchedule,
    vehicle,
    connectivity: connectivity?.[0],
    isInActiveDREvent,
    hasDREventAlert,
    optedOutOfActiveEvent,
    hasDeviceAlertBanner: getHasDeviceAlertBanner(vehicle),
    hasPendingRatePlan: Boolean(hasPendingRatePlan),
    forecastHasNoHome: forecastForVehicle?.type === 'no home',
    batteryPercent,
    chargeLimitPercent,
    activeSettings: getActiveChargeFlags(activeChargeSettings?.[0]),
    now,
  })

  const showForecastDetails =
    Boolean(chargeForecast) && status.layout === 'full'

  const segments =
    chargeForecast && showForecastDetails
      ? getChargeSegments({
          chargeForecast,
          now,
          batteryPercent:
            batteryPercent === null ? null : Math.round(batteryPercent),
          chargeLimitPercent,
          isOverride: status.kind === 'Override',
          showRateTiers: status.showRateTiers,
        })
      : []

  const chart =
    chargeForecast && showForecastDetails
      ? getChargeChart({ chargeForecast, now, chargeLimitPercent })
      : null

  const upcomingRates =
    chargeForecast && status.showUpcomingRates
      ? getUpcomingRates({
          periods: chargeForecast.charge_forecast_periods,
          planKind: getRatePlanKind(chargeForecast.plan),
          now,
          realTimeBoundariesCents: getRealTimeBoundariesCents(
            chargeForecast.plan,
          ),
        })
      : null

  const onAlertAction = (action: StatusAlertAction) => {
    logEvent(`chargeforecast_v2_alert_${action}`)
    match(action)
      .with('chooseGoal', () =>
        navigation.push(`/devices/vehicle/${vehicleId}/goals`),
      )
      .with('fixConnection', () =>
        navigation.push(`/devices/vehicle/${vehicleId}/connectivity`),
      )
      .with('turnOnChargeControl', () =>
        navigation.push(`/devices/vehicle/${vehicleId}?tab=settings`),
      )
      .with('addRatePlan', () => navigation.push('/manage-utility-information'))
      .with('addHome', () => navigation.push('/settings?action=home'))
      .with('reportIssue', () => setReportIssueOpen(true))
      .with('learnMoreChargeShortfall', () =>
        externallyNavigate(MISSED_CHARGE_TARGET_HELP_URL),
      )
      .exhaustive()
  }

  const onSubmitReportIssue = (message: string) => {
    const context = REPORT_CONTEXT[status.reportTopic ?? 'forecastFailed']
    logEvent(`chargeforecast_v2_report_${status.reportTopic ?? 'unknown'}`)
    dispatch(
      submitContactFormWithAuth({
        title: context.subject,
        message: `${message}\n\n${context.detail}`,
      }),
    )
    setReportIssueOpen(false)
    setReportIssueSent(true)
  }

  const openWhyModal = () => {
    logEvent('chargeforecast_v2_why_opened')
    setWhyModalOpen(true)
  }

  const closeWhyModal = () => setWhyModalOpen(false)

  const onManageSettings = () => {
    setWhyModalOpen(false)
    navigation.push(`/devices/vehicle/${vehicleId}/goals`)
  }

  return {
    isLoading:
      !chargeForecasts?.length &&
      [RequestStatus.Idle, RequestStatus.Loading].includes(
        chargeForecastStatus,
      ),
    status,
    batteryPercent,
    segments,
    chargingWindow: chargeForecast ? getChargingWindow(chargeForecast) : null,
    chart,
    upcomingRates,
    showChargeButton: getShowChargeButton(
      status,
      chargeForecast?.charge_now_status,
    ),
    setDraggedChargeLimit,
    onAlertAction,
    whyModalOpen,
    openWhyModal,
    closeWhyModal,
    onManageSettings,
    reportIssueOpen,
    reportIssueSent,
    closeReportIssue: () => setReportIssueOpen(false),
    onSubmitReportIssue,
  }
}
